import { mm } from "@inovitas/infra3dsdk";
import { Colorize, ColorizeOutlined, ContentCopy } from "@mui/icons-material";
import {
  Box,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { defaultStore } from "shared/common/stores/defaultStore";
import { sidebarStore } from "shared/common/stores/sidebarStore";
import { lineIntersectsCircle } from "shared/common/utils/geometry/intersections";
import MeasureResultField from "./MeasureResultField";
import { monitor } from "shared/common/services/apm-logging";

interface axisOption {
  name: string;
  disabled?: boolean;
}

interface resultField {
  id: string;
  label: string;
  value: number | string;
}

export default function LinrefContent() {
  const viewer = defaultStore((state) => state.viewer);
  const darkTheme = defaultStore((state) => state.darkTheme);
  const axisCollection = defaultStore((state) => state.axisCollection);
  const networkLayer = defaultStore((state) => state.networkLayer);
  const clientConfig = defaultStore((state) => state.clientConfig);

  const { t } = useTranslation();
  const selectedAxis = sidebarStore((state) => state.selectedAxis);
  const changeSelectedAxis = sidebarStore((state) => state.changeSelectedAxis);
  const measurementActiveTool = sidebarStore(
    (state) => state.measurementActiveTool
  );
  const linrefActiveAttributes = sidebarStore(
    (state) => state.linrefActiveAttributes
  );
  const changeLinrefActiveAttributes = sidebarStore(
    (state) => state.changeLinrefActiveAttributes
  );
  const linrefToolValues = sidebarStore((state) => state.linrefToolValues);
  const linrefTool = sidebarStore((state) => state.linrefTool);
  const showSmallToolContent = sidebarStore(
    (state) => state.showSmallToolContent
  );
  const apm = defaultStore((state) => state.apm);
  const changeAlertMessage = defaultStore((state) => state.changeAlertMessage);

  const [axisOptions, setAxisOptions] = useState<axisOption[]>([]);
  const [axisPickToolEnabled, setAxisPickToolEnabled] = useState<boolean>();
  const [resultFields, setResultFields] = useState<resultField[]>([]);

  const axisPickToolRef = useRef<any>(null);

  // Memoize axisCollection for axis in render distance
  const visibleAxisCollection = useRef<mm.AxisObject[]>([]);

  useEffect(() => {
    if (linrefActiveAttributes.length !== 0) return;
    if (clientConfig?.linref?.type === "rail-ch") {
      changeLinrefActiveAttributes(["v"]);
    } else {
      changeLinrefActiveAttributes(["u"]);
    }
  }, [clientConfig?.linref?.type]);

  useEffect(() => {
    const geoframe = viewer?.getScene().getCurrentGeoframe();
    if (!geoframe) return;
    visibleAxisCollection.current = axisCollection.filter((row) => {
      if (!row.axis?.axis?.features) return false;

      // Check if any segment of the axis intersects the geoframe radius
      const radius = clientConfig?.render_distance || 100;
      const center = { x: geoframe.easting, y: geoframe.northing };

      for (const feature of row.axis?.axis?.features) {
        const coords = feature.geometry.coordinates;
        if (lineIntersectsCircle(coords, center, radius)) return true;
      }
      return false;
    });
  }, [axisCollection, viewer?.getScene().getCurrentGeoframe()]);

  useEffect(() => {
    const fields: resultField[] = [
      {
        id: "u",
        label: t("measurement.linref_u"),
        value: (linrefToolValues?.lrcoords[0] || 0).toFixed(2) + " m",
      },
      {
        id: "v",
        label: t("measurement.linref_v"),
        value: (linrefToolValues?.lrcoords[1] || 0).toFixed(2) + " m",
      },
      {
        id: "w",
        label: t("measurement.linref_w"),
        value: (linrefToolValues?.lrcoords[2] || 0).toFixed(2) + " m",
      },
    ];
    if (clientConfig?.linref?.type === "rail-ch") {
      // Move 'u' to the end as least important in rail applications
      // but most important in road applications
      fields.splice(0, 1);
      fields.push({
        id: "u",
        label: t("measurement.linref_u"),
        value: (linrefToolValues?.lrcoords[0] || 0).toFixed(2) + " m",
      });
    }
    if (clientConfig?.linref?.ref_point_attribute) {
      if (linrefToolValues?.featureidx === undefined) {
        fields.splice(0, 0, {
          id: "ref_point",
          label: t("linref.ref_point"),
          value: "",
        });
      } else {
        const matchedAxis = visibleAxisCollection.current.find(
          (axis) => axis.name === selectedAxis?.name
        );
        const axisid_obj = matchedAxis?.axis.getAxisIdentifier(
          linrefToolValues.featureidx
        );
        const axisid = Object.keys(axisid_obj || {})
          .map((x) => {
            return axisid_obj[x];
          })
          .slice(-1);
        fields.splice(0, 0, {
          id: "ref_point",
          label: t("linref.ref_point"),
          value: axisid.toString() || "",
        });
      }
    }
    setResultFields(fields);
  }, [linrefToolValues, t]);

  useEffect(() => {
    const options = visibleAxisCollection.current
      .map((axis) => {
        return {
          name: axis.axis?.name,
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
    setAxisOptions(options);
    if (!viewer) return;

    // set measurementAxis = selectedAxis from AxisMenu on "first load"
    if (selectedAxis) {
      viewer.getScene().getLRHandler().setActiveMeasurementAxis(selectedAxis);
    } else {
      // set measurementAxis = navigationAxis on if no axis is selected
      const defaultAxis = viewer
        .getScene()
        .getLRHandler()
        .getActiveNavigationAxis();
      viewer.getScene().getLRHandler().setActiveMeasurementAxis(defaultAxis);
      if (!defaultAxis) return;
      changeSelectedAxis(defaultAxis);
    }
  }, [visibleAxisCollection.current, selectedAxis]);

  useEffect(() => {
    removeAxisPickTool();
    setAxisPickToolEnabled(false);
  }, [measurementActiveTool]);

  const createAxisPickTool = () => {
    if (!viewer) return;
    const tooloptions = {
      selectedcbf: onViewerSelect,
      onovercbf: (feature_hovered: mm.Feature) => {
        const axis = visibleAxisCollection.current.find((axis) =>
          axis.axis?.axis?.features.some(
            (feature: mm.Feature) => feature.id === feature_hovered.id
          )
        );
        if (!axis) return;
        networkLayer?.addAxisToSelection(axis.axis);
      },
      onoutcbf: (feature_out: mm.Feature) => {
        const axis = visibleAxisCollection.current.find((axis) =>
          axis.axis?.axis?.features.some(
            (feature: mm.Feature) => feature.id === feature_out.id
          )
        );
        if (!axis) return;
        networkLayer?.removeAxisFromSelection(axis.axis);
      },
      hightlightonhover: false,
      activecursor: "pointer",
      clearonnullpick: true,
      selectionmodel: mm.SELECTIONMODEL.SINGLE,
    };

    const _axisPickTool = viewer
      .getScene()
      .getToolHandler()
      .createSelectFeatureTool(tooloptions, "axisPick");

    viewer
      .getScene()
      .getLayerHandler()
      .layers.forEach((layer: mm.Layer) => {
        if (layer.type === mm.LAYERTYPE.NETWORKLAYER)
          _axisPickTool.addToPickLayer(layer.getFeatureCollection());
      });

    _axisPickTool.enable();

    axisPickToolRef.current = _axisPickTool;
  };

  function removeAxisPickTool() {
    if (!viewer || !axisPickToolRef.current) return;

    axisPickToolRef.current.disable();
    viewer.getScene().getToolHandler().removeTool(axisPickToolRef.current);
    axisPickToolRef.current = null;
  }

  function enablePick() {
    // Preserve the current state of the linref tool
    // Makes it possible to change the axis while the measured point stays the same
    if (linrefTool) {
      const state = linrefTool.toolstate;
      linrefTool.disable();
      linrefTool.toolstate = state;
    }
    createAxisPickTool();
    setAxisPickToolEnabled(true);
  }

  function disablePick() {
    linrefTool?.enable();
    removeAxisPickTool();
    setAxisPickToolEnabled(false);
  }

  const handlePickAxis = () => {
    if (!viewer) return;

    if (axisPickToolRef.current) {
      disablePick();
    } else {
      enablePick();
    }
  };

  const handleAxisChange = (event: SelectChangeEvent<string>) => {
    const matchedAxis = visibleAxisCollection.current.find(
      (axis) => axis.name === event.target.value
    );
    if (!viewer || !matchedAxis) return;
    networkLayer?.featureselector.clearSelection();
    networkLayer?.addAxisToSelection(matchedAxis.axis);
    changeSelectedAxis(matchedAxis.axis);
    viewer.getScene().getLRHandler().setActiveMeasurementAxis(matchedAxis.axis);
  };

  const onViewerSelect = (info: mm.Feature) => {
    disablePick();
    const matchedAxis = visibleAxisCollection.current.find(
      (axis: mm.AxisObject) =>
        axis.axis?.axis?.features.some(
          (feature: mm.Feature) => feature.id === info.id
        )
    );
    if (!viewer || !matchedAxis) return;
    networkLayer?.featureselector.clearSelection();
    networkLayer?.addAxisToSelection(matchedAxis.axis);
    changeSelectedAxis(matchedAxis.axis);
    viewer.getScene().getLRHandler().setActiveMeasurementAxis(matchedAxis.axis);
  };

  return (
    <>
      {visibleAxisCollection.current.length === 0 ? (
        <Typography
          variant="body2"
          sx={{
            fontWeight: "400",
            color: darkTheme ? "white" : "darkblue.main",
          }}
        >
          {t("measurement.linref_no_axis")}
        </Typography>
      ) : (
        <>
          {!showSmallToolContent && (
            <Box sx={{ paddingBlockEnd: 0.5 }}>
              <Typography
                sx={{
                  fontSize: "16px",
                  color: darkTheme ? "white" : "darkblue.main",
                  fontWeight: "700",
                }}
              >
                {t("linref.axis")}
              </Typography>
              <Box sx={{ display: "flex" }}>
                <Box sx={{ position: "relative", flex: 1, display: "flex" }}>
                  <Select
                    value={selectedAxis ? selectedAxis.name : ""}
                    size="small"
                    onChange={handleAxisChange}
                    sx={{
                      flex: 1,
                      maxWidth: 314,
                      color: darkTheme ? "white" : "darkblue.main",
                      ".MuiSelect-select": {
                        minHeight: "auto", // Remove default min-height
                        paddingLeft: "8px",
                      },
                      ".MuiOutlinedInput-notchedOutline": {
                        borderColor: darkTheme ? "white" : "darkblue.main", // Border color
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: darkTheme ? "white" : "darkblue.main", // Hover border color
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: darkTheme ? "white" : "darkblue.main", // Focus border color
                      },
                      "& .MuiSvgIcon-root": {
                        color: darkTheme ? "white" : "darkblue.main", // Dropdown icon color
                      },
                      "& .MuiSelect-placeholder": {
                        color: darkTheme ? "white" : "darkblue.main", // Placeholder color
                      },
                    }}
                  >
                    {axisOptions.map((option, index) => (
                      <MenuItem key={index} value={option.name}>
                        <Typography variant="body2">{option.name}</Typography>
                      </MenuItem>
                    ))}
                  </Select>
                  <IconButton
                    disableRipple
                    disableFocusRipple
                    onClick={() =>
                      monitor(apm, `copy-value | axisname`, () => {
                        navigator.clipboard.writeText(selectedAxis?.name || "");
                        changeAlertMessage({
                          message: t("alert_success.copying_of_values"),
                          type: "success",
                        });
                      })
                    }
                    size="small"
                    sx={{
                      position: "absolute",
                      right: 30,
                      flexShrink: 0,
                      color: darkTheme ? "white" : "darkblue.main",
                      "@media (hover: hover)": {
                        ":hover": { color: "secondary.main" },
                      },
                    }}
                  >
                    <ContentCopy />
                  </IconButton>
                </Box>
                <Tooltip
                  title={t("measurement.linref_pick_tooltip")}
                  placement="right"
                  enterDelay={400}
                  classes={{
                    tooltip: "infra3d-buttonDescription",
                  }}
                >
                  <IconButton
                    disableRipple
                    disableFocusRipple
                    size="small"
                    onClick={handlePickAxis}
                    sx={{
                      color: axisPickToolEnabled
                        ? "secondary.main"
                        : darkTheme
                          ? "white"
                          : "darkblue.main",
                      "@media (hover: hover)": {
                        ":hover": { color: "secondary.main" },
                      },
                    }}
                  >
                    {axisPickToolEnabled ? <Colorize /> : <ColorizeOutlined />}
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          )}
          <Box sx={{ display: "grid", gap: 0.5 }}>
            {resultFields
              .filter((item: any) => {
                if (showSmallToolContent) {
                  return linrefActiveAttributes.includes(item.id);
                } else {
                  return item;
                }
              })
              .map((item: any) => (
                <MeasureResultField
                  key={item.id}
                  id={item.id}
                  title={item.label}
                  value={item.value}
                  loggingString={item.id}
                  activeAttributes={linrefActiveAttributes}
                  changeActiveAttributes={changeLinrefActiveAttributes}
                  showPin={resultFields.length > 1}
                />
              ))}
          </Box>
        </>
      )}
    </>
  );
}
